import React, { useState } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { CreditCard, Trash2, AlertTriangle, Loader2 } from 'lucide-react';
import { formatCurrency, getStatusLabel, getVehicleTypeLabel } from '../../lib/utils';

export default function OrdersTable({ orders, onStatusChange, onCharge, onDelete, canDelete = false }) {
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [reason, setReason] = useState('');
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState('');

  const openDelete = (order) => {
    setDeleteTarget(order);
    setReason('');
    setError('');
  };

  const confirmDelete = async () => {
    const trimmed = reason.trim();
    if (trimmed.length < 3) {
      setError('El motivo es obligatorio (mínimo 3 caracteres)');
      return;
    }
    setDeleting(true);
    setError('');
    try {
      await onDelete(deleteTarget.id, trimmed);
      setDeleteTarget(null);
      setReason('');
    } catch (e) {
      setError(e?.response?.data?.detail || 'Error al eliminar la venta');
    }
    setDeleting(false);
  };

  if (orders.length === 0) {
    return <p className="text-slate-500 text-center py-8">No hay órdenes hoy</p>;
  }

  return (
    <>
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead>
            <tr className="border-b border-slate-200">
              <th className="table-header">Folio</th>
              <th className="table-header">Cliente</th>
              <th className="table-header">Vehículo</th>
              <th className="table-header">Total</th>
              <th className="table-header">Pago</th>
              <th className="table-header">Estado</th>
              <th className="table-header">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((order) => (
              <tr key={order.id} className="table-row">
                <td className="table-cell font-mono text-blue-600">{order.folio}</td>
                <td className="table-cell">
                  <div className="font-medium">{order.client?.name}</div>
                  <div className="text-xs text-slate-500">{order.client?.phone}</div>
                </td>
                <td className="table-cell">
                  {getVehicleTypeLabel(order.vehicle?.type)}
                  {order.vehicle?.plates && (
                    <div className="text-xs text-slate-500">{order.vehicle.plates}</div>
                  )}
                </td>
                <td className="table-cell font-semibold">{formatCurrency(order.total)}</td>
                <td className="table-cell">
                  <span
                    className={`status-badge ${
                      order.payment_status === 'pagado' ? 'status-listo' : 'status-recibido'
                    }`}
                  >
                    {getStatusLabel(order.payment_status)}
                  </span>
                </td>
                <td className="table-cell">
                  <span className={`status-badge status-${order.service_status}`}>
                    {getStatusLabel(order.service_status)}
                  </span>
                </td>
                <td className="table-cell">
                  <div className="flex items-center gap-2">
                    <Select
                      value={order.service_status}
                      onValueChange={(value) => onStatusChange(order.id, value)}
                    >
                      <SelectTrigger className="w-32" data-testid={`status-select-${order.id}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="recibido">Recibido</SelectItem>
                        <SelectItem value="en_proceso">En Proceso</SelectItem>
                        <SelectItem value="listo">Listo</SelectItem>
                        <SelectItem value="entregado">Entregado</SelectItem>
                      </SelectContent>
                    </Select>
                    {order.payment_status === 'pendiente' && onCharge && (
                      <Button
                        type="button"
                        size="sm"
                        className="gap-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                        onClick={() => onCharge(order)}
                        data-testid={`charge-order-${order.id}`}
                      >
                        <CreditCard className="w-3.5 h-3.5" />
                        Cobrar
                      </Button>
                    )}
                    {canDelete && onDelete && (
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => openDelete(order)}
                        data-testid={`delete-order-${order.id}`}
                        title="Eliminar venta"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={!!deleteTarget} onOpenChange={(open) => { if (!open) { setDeleteTarget(null); setReason(''); setError(''); } }}>
        <DialogContent className="max-w-md" data-testid="delete-order-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" />
              Eliminar venta
            </DialogTitle>
            <DialogDescription>
              Esta acción marcará la orden como <b>cancelada</b>. Los puntos de lealtad se revertirán automáticamente si la venta estaba pagada.
            </DialogDescription>
          </DialogHeader>

          {deleteTarget && (
            <div className="p-3 rounded-lg bg-slate-50 border border-slate-200 text-sm space-y-1">
              <div><span className="text-slate-500">Folio:</span> <span className="font-mono font-bold text-blue-700">{deleteTarget.folio}</span></div>
              <div><span className="text-slate-500">Cliente:</span> {deleteTarget.client?.name || '—'}</div>
              <div><span className="text-slate-500">Total:</span> <span className="font-semibold">{formatCurrency(deleteTarget.total)}</span></div>
              <div><span className="text-slate-500">Pago:</span> {getStatusLabel(deleteTarget.payment_status)}</div>
            </div>
          )}

          <div>
            <Label htmlFor="delete-reason">Motivo <span className="text-red-500">*</span></Label>
            <Input
              id="delete-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ej: error de cobro, duplicado, cancelación del cliente..."
              className="mt-1"
              data-testid="delete-reason-input"
              autoFocus
            />
            {error && <p className="text-red-600 text-xs mt-1" data-testid="delete-reason-error">{error}</p>}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => { setDeleteTarget(null); setReason(''); setError(''); }}
              disabled={deleting}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              className="bg-red-600 hover:bg-red-700 text-white gap-2"
              onClick={confirmDelete}
              disabled={deleting}
              data-testid="confirm-delete-order-btn"
            >
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
              Eliminar venta
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
